import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  signal,
  viewChild,
} from '@angular/core';
import { TagComponent } from 'src/app/shared/components/tag/tag.component';
import { AlertReportDetailDto } from 'src/generated-sources/openapi/alert-service/alert';
import { AlertReportTag } from '../models/wallet-alert-report.models';
import { transformAlertData } from './alert-indicators-tags.utils';

@Component({
  selector: 'app-alert-indicators-tags',
  imports: [TagComponent],
  templateUrl: './alert-indicators-tags.component.html',
  styleUrl: './alert-indicators-tags.component.scss',
})
export class AlertIndicatorsTagsComponent {
  private readonly ngZone = inject(NgZone);
  data = input.required<AlertReportDetailDto[]>();

  protected displayTags = signal<AlertReportTag[]>([]);
  protected isReady = signal(false);

  private readonly tagsContainer = viewChild<ElementRef>('tagsContainer');
  private readonly initialTags = computed(() => transformAlertData(this.data()));

  constructor() {
    effect(() => {
      this.displayTags.set(this.initialTags());
      this.isReady.set(false);

      queueMicrotask(() => {
        const container = this.tagsContainer()?.nativeElement;
        if (container) {
          this.updateVisibility(container.offsetWidth);
        }
      });
    });

    this.setupResizeObserver();
  }

  private setupResizeObserver(): void {
    effect((onCleanup) => {
      const container = this.tagsContainer()?.nativeElement;
      if (!container) return;

      this.ngZone.runOutsideAngular(() => {
        const observer = new ResizeObserver((entries) => {
          const width = entries[0].contentRect.width;
          this.updateVisibility(width);
        });

        observer.observe(container);
        onCleanup(() => observer.disconnect());
      });
    });
  }

  private updateVisibility(availableWidth: number): void {
    const { updatedTags, needsUpdate } = this.calculateVisibility(availableWidth);

    if (needsUpdate || !this.isReady()) {
      this.ngZone.run(() => {
        this.displayTags.set(updatedTags);
        this.isReady.set(true);
      });
    }
  }

  private calculateVisibility(available: number) {
    const container = this.tagsContainer()?.nativeElement;
    const tagElements = container.querySelectorAll('app-tag');
    const tags = this.displayTags();
    const gap = 6;
    const extraTagWidthInPx = 40;
    let currentX = 0;
    let needsUpdate = false;

    const updatedTags = tags.map((tag, index) => {
      const el = tagElements[index] as HTMLElement;
      const isLastTag = index === tags.length - 1;
      const additionalWidth = !isLastTag ? extraTagWidthInPx : 0;

      if (el && tag.measuredWidth === 0) {
        tag.measuredWidth = el.offsetWidth + gap;
      }

      currentX += tag.measuredWidth;
      const shouldBeVisible = currentX + additionalWidth < available;

      if (tag.visible !== shouldBeVisible) {
        needsUpdate = true;
        return { ...tag, visible: shouldBeVisible };
      }
      return tag;
    });

    return { updatedTags, needsUpdate };
  }

  protected readonly hiddenTagsCount = computed(
    () => this.displayTags().filter((t) => !t.visible).length,
  );

  protected readonly hiddenTagsText = computed(() => {
    const count = this.hiddenTagsCount();
    if (count === this.displayTags().length) {
      return $localize`:@@detectedChanges:${count}:INTERPOLATION: cambios detectados`;
    }
    return `+${count}`;
  });
}
